import dicomParser from "dicom-parser";
import { TAG_DICT } from "./DataDictionary";
import axiosInterceptor from "../../../helpers/Interceptor";
import { APIs } from "../../../constants/Constants";

/**
 * Parses a DICOM Structured Report into a render-ready model.
 *
 * The SR content tree (Content Sequence 0040,A730, nested to any depth) is
 * flattened depth-first into rows that keep their depth, so the viewer can
 * indent each content item under its parent the way the document is
 * structured - the same layout the Advance Viewer shows.
 *
 * Returned shape:
 *   {
 *     title: string,                                  // root Concept Name
 *     subtitle: string,                               // station + content date
 *     sections: [{ title, rows: [{ label, value }] }], // patient/study header
 *     items: [{ depth, number, label, value, isContainer }], // content tree;
 *                                                     // number is 1-based among siblings
 *   }
 */

const formatDate = (date) => {
  if (!date) return "";
  return date.slice(0, 4) + "-" + date.slice(4, 6) + "-" + date.slice(6, 8);
};

const formatTime = (time) => {
  if (!time) return "";
  const t = time.includes(":") ? time.replace(/:/g, "") : time;
  let out = t.slice(0, 2);
  if (t.length >= 4) out += ":" + t.slice(2, 4);
  if (t.length >= 6) out += ":" + t.slice(4, 6);
  return out;
};

const joinDateTime = (date, time, separator = " ") =>
  [formatDate(date), formatTime(time)].filter(Boolean).join(separator);

// DT content items render ISO-style ("2025-09-08T15:38:08"), as the Advance
// Viewer shows them.
const formatDateTime = (dt) => {
  if (!dt) return "";
  const time = dt.slice(8).split(/[+-]/)[0].split(".")[0];
  return joinDateTime(dt.slice(0, 8), time, "T");
};

const formatPersonName = (pn) =>
  pn ? pn.split("=")[0].split("^").filter(Boolean).join(" ") : "";

const safe = (v) => (v ? v : "");

const firstItem = (dataSet, tag) =>
  dataSet?.elements?.[tag]?.items?.[0]?.dataSet;

// Code Meaning, falling back to the code value when a sender leaves it empty.
const codeText = (codeDataSet) =>
  codeDataSet
    ? safe(codeDataSet.string("x00080104")) ||
      safe(codeDataSet.string("x00080100"))
    : "";

// Matches the Advance Viewer: "512.00" -> "512.0", "5" -> "5.0", "3.3230" -> "3.323".
const formatNumber = (raw) => {
  const text = safe(raw).trim();
  const n = Number(text);
  if (text === "" || !Number.isFinite(n)) return text;
  return Number.isInteger(n) ? n.toFixed(1) : String(n);
};

const numericValue = (item) => {
  const measured = firstItem(item, "x0040a300");
  if (!measured) {
    // Empty NUM: the qualifier says why (e.g. "Not a number").
    return codeText(firstItem(item, "x0040a301"));
  }
  const value = formatNumber(measured.string("x0040a30a"));
  const unitsCode = firstItem(measured, "x004008ea");
  // Units are shown as their UCUM code ("mGy.cm", "{events}"), as the Advance
  // Viewer does; "1" means dimensionless and is left off.
  const unitValue = safe(unitsCode?.string("x00080100"));
  const units = unitValue === "1" ? "" : unitValue || codeText(unitsCode);
  return units ? `${value} ${units}` : value;
};

const referencedSop = (item) => {
  const ref = firstItem(item, "x00081199");
  if (!ref) return "";
  const uid = safe(ref.string("x00081155"));
  const frames = safe(ref.string("x00081160"));
  return frames ? `${uid} (frame ${frames})` : uid;
};

const contentItemValue = (item, valueType) => {
  switch (valueType) {
    case "TEXT":
      return safe(item.string("x0040a160"));
    case "CODE":
      return codeText(firstItem(item, "x0040a168"));
    case "NUM":
      return numericValue(item);
    case "DATETIME":
      return formatDateTime(item.string("x0040a120"));
    case "DATE":
      return formatDate(item.string("x0040a121"));
    case "TIME":
      return formatTime(item.string("x0040a122"));
    case "PNAME":
      return formatPersonName(item.string("x0040a123"));
    case "UIDREF":
      return safe(item.string("x0040a124"));
    case "IMAGE":
    case "COMPOSITE":
    case "WAVEFORM":
      return referencedSop(item);
    case "SCOORD":
    case "SCOORD3D":
      return safe(item.string("x00700023"));
    default:
      return "";
  }
};

const MAX_DEPTH = 64;

const walkContent = (contentSequence, depth, items) => {
  if (!contentSequence?.items || depth > MAX_DEPTH) return;
  let number = 0;
  for (const { dataSet: item } of contentSequence.items) {
    if (!item) continue;
    number += 1;
    const valueType = safe(item.string("x0040a040"));
    const label = codeText(firstItem(item, "x0040a043"));

    // By-reference relationship: points at another node instead of holding one.
    const refId = item.elements["x0040db73"];
    if (refId && !valueType) {
      const ids = [];
      for (let i = 0; i < refId.length / 4; i++) ids.push(item.uint32("x0040db73", i));
      items.push({
        depth,
        number,
        label: safe(item.string("x0040a010")) || "Reference",
        value: `item ${ids.join(".")}`,
        isContainer: false,
      });
      continue;
    }

    const isContainer = valueType === "CONTAINER";
    items.push({
      depth,
      number,
      label,
      value: isContainer ? "" : contentItemValue(item, valueType),
      isContainer,
    });
    walkContent(item.elements["x0040a730"], depth + 1, items);
  }
};

export const parseSRDataSet = (dataSet) => {
  const sections = [
    {
      title: "Patient",
      rows: [
        { label: "Patient's Name", value: formatPersonName(dataSet.string("x00100010")) },
        { label: "Patient ID", value: safe(dataSet.string("x00100020")) },
        { label: "Patient's Birth Date", value: formatDate(dataSet.string("x00100030")) },
        { label: "Patient's Sex", value: safe(dataSet.string("x00100040")) },
      ],
    },
    {
      title: "Study",
      rows: [
        {
          label: "Study Date",
          value: joinDateTime(dataSet.string("x00080020"), dataSet.string("x00080030")),
        },
        { label: "Study ID", value: safe(dataSet.string("x00200010")) },
        { label: "Accession Number", value: safe(dataSet.string("x00080050")) },
        {
          label: "Referring Physician's Name",
          value: formatPersonName(dataSet.string("x00080090")),
        },
      ],
    },
    {
      title: "Report Status",
      rows: [
        { label: "Completion Flag", value: safe(dataSet.string("x0040a491")) },
        { label: "Verification Flag", value: safe(dataSet.string("x0040a493")) },
      ],
    },
  ];

  const items = [];
  walkContent(dataSet.elements["x0040a730"], 0, items);

  return {
    title: codeText(firstItem(dataSet, "x0040a043")),
    // Line under the title: station and content date/time.
    subtitle: [
      safe(dataSet.string("x00081010")),
      joinDateTime(dataSet.string("x00080023"), dataSet.string("x00080033")),
    ]
      .filter(Boolean)
      .join(" "),
    sections,
    items,
  };
};

export const generateSRReport = async (path, setSRReport) => {
  let res;
  try {
    res = await axiosInterceptor.get(path, { responseType: "blob" });
  } catch (error) {
    if (error?.response?.status === 403)
      window.location.assign(APIs.REDIRECTLOGOUT);
    return error?.response;
  }

  const reader = new FileReader();
  reader.onload = function () {
    // dicomParser requires a Uint8Array rather than the raw ArrayBuffer.
    const byteArray = new Uint8Array(reader.result);
    try {
      const options = {
        untilTag: "",
        vrCallback(tag) {
          const formatted = `(${tag.substring(1, 5).toUpperCase()},${tag
            .substring(5, 9)
            .toUpperCase()})`;
          return !!TAG_DICT[formatted] ? TAG_DICT[formatted].vr : undefined;
        },
      };

      const dataSet = dicomParser.parseDicom(byteArray, options);
      setSRReport(parseSRDataSet(dataSet));
    } catch (error) {
      console.error("Failed to parse SR", error);
      setSRReport(null);
    }
  };

  reader.readAsArrayBuffer(res.data);
};
